import { ReactNode, useRef } from 'react';
import styled from 'styled-components';
import noop from 'lodash/noop';

import { Caret } from 'shared/icons/Caret';

interface ICollapseToggleProps {
  className?: string;
  'data-qa'?: string;
  disabled?: boolean;
  expanded?: boolean;
  subtitle?: string | ReactNode;
  title?: string | ReactNode;
  onToggleClick?: () => void;
}

interface ICollapseBoxProps {
  children: ReactNode;
  className?: string;
  expanded: boolean;
}

export const CollapseToggle = ({
  'data-qa': dataQa,
  className,
  disabled,
  expanded,
  onToggleClick = noop,
  subtitle,
  title,
}: ICollapseToggleProps) => {
  return (
    <CollapseToggle.Wrapper
      className={className}
      onClick={disabled ? noop : onToggleClick}
      data-qa={dataQa}
    >
      <CollapseToggle.TitleWrapper>
        <CollapseToggle.Title>{title}</CollapseToggle.Title>
        {!disabled && (
          <CollapseToggle.Caret>
            <Caret direction={expanded ? 'up' : 'down'} />
          </CollapseToggle.Caret>
        )}
      </CollapseToggle.TitleWrapper>
      {subtitle && <CollapseToggle.Subtitle>{subtitle}</CollapseToggle.Subtitle>}
    </CollapseToggle.Wrapper>
  );
};

export const CollapseBox = ({ children, className, expanded }: ICollapseBoxProps) => {
  const contentRef = useRef(null);
  const contentHeight = contentRef?.current?.clientHeight;

  return (
    <CollapseBox.Wrapper className={className} $expanded={expanded} $maxHeight={contentHeight}>
      <CollapseBox.Content ref={contentRef}>{children}</CollapseBox.Content>
    </CollapseBox.Wrapper>
  );
};

CollapseBox.Wrapper = styled.div<{ $expanded: boolean; $maxHeight: number }>`
  overflow: hidden;
  transition: 1s max-height;
  max-height: ${({ $expanded, $maxHeight }) => ($expanded ? $maxHeight : 0)};
`;

CollapseBox.Content = styled.div`
  overflow: auto;
`;

CollapseToggle.Wrapper = styled.div`
  display: grid;
  grid-template-columns: [title] auto [subtitle-start] auto [subtitle-end];
  cursor: pointer;
`;

CollapseToggle.TitleWrapper = styled.div`
  display: flex;
`;

CollapseToggle.Title = styled.div`
  grid-column-start: title;
  grid-column-end: subtitle-start;
  grid-row: 1;
`;

CollapseToggle.Subtitle = styled.div`
  grid-column-start: subtitle-end;
  grid-column-end: subtitle-start;
  text-align: right;
`;

CollapseToggle.Caret = styled.div`
  text-align: left;
  margin-left: 8px;
`;
